import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../../stores/authStore'
import { Calendar, CheckCircle, Clock, XCircle, Video, ClipboardList, ChevronRight } from 'lucide-react'
import { interviewerApi } from '../../lib/api/interviewer'

interface DashboardStats {
  upcoming: number
  today: number
  completed: number
  cancelled: number
  pending_evaluations: number
  avg_rating: number | null
}

interface InterviewItem {
  id: number
  client_id: number | null
  client_name: string | null
  client_email: string | null
  scheduled_at: string
  duration_minutes: number
  status: string
  zoom_join_url: string | null
  zoom_password: string | null
  evaluation_id: number | null
}

type FilterType = 'all' | 'upcoming' | 'completed'

function parseDate(iso: string) {
  const d = new Date(iso)
  const now = new Date()
  return {
    d,
    date: d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' }),
    time: d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true }),
    isPast: d < now,
    isToday: d.toDateString() === now.toDateString(),
  }
}

const STATUS_LABEL: Record<string, string> = {
  confirmed: 'Upcoming',
  completed: 'Completed',
  cancelled: 'Cancelled',
  in_progress: 'In Progress',
}

export default function InterviewerHome() {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [stats, setStats] = useState<DashboardStats>({
    upcoming: 0,
    today: 0,
    completed: 0,
    cancelled: 0,
    pending_evaluations: 0,
    avg_rating: null,
  })
  const [interviews, setInterviews] = useState<InterviewItem[]>([])
  const [filter, setFilter] = useState<FilterType>('all')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    interviewerApi
      .getDashboard()
      .then((data) => {
        setStats({
          upcoming: data.stats?.upcoming ?? 0,
          today: data.stats?.today ?? 0,
          completed: data.stats?.completed ?? 0,
          cancelled: data.stats?.cancelled ?? 0,
          pending_evaluations: data.stats?.pending_evaluations ?? 0,
          avg_rating: data.stats?.avg_rating ?? null,
        })
        setInterviews(data.upcoming_interviews ?? [])
      })
      .catch(() => setError('Failed to load dashboard data.'))
      .finally(() => setLoading(false))
  }, [])

  function openEvaluation(item: InterviewItem) {
    navigate(`/interviewer/evaluations?bookingId=${item.id}`)
  }

  const filtered = interviews.filter((item) => {
    const { isPast } = parseDate(item.scheduled_at)
    if (filter === 'upcoming') return !isPast
    if (filter === 'completed') return isPast
    return true
  })

  if (loading) return <div className="loading-screen">Loading dashboard...</div>

  if (error) {
    return (
      <div className="empty-state">
        <XCircle size={48} />
        <h3>{error}</h3>
      </div>
    )
  }

  return (
    <div>
      {/* Header */}
      <div className="page-header">
        <h1 className="page-title">
          Welcome back{user?.name ? `, ${user.name}` : ''}
        </h1>
        <p className="page-subtitle">
          Manage your scheduled interviews and track session progress.
        </p>
      </div>

      {/* Stats — Upcoming / Completed / Cancelled / Pending Eval */}
      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)' }}>
        <div className="stat-card">
          <div className="icon primary">
            <Calendar size={22} />
          </div>
          <div className="value">{stats.upcoming}</div>
          <div className="label">Upcoming</div>
        </div>

        <div className="stat-card">
          <div className="icon success">
            <CheckCircle size={22} />
          </div>
          <div className="value">{stats.completed}</div>
          <div className="label">Completed</div>
        </div>

        <div className="stat-card">
          <div className="icon danger">
            <XCircle size={22} />
          </div>
          <div className="value">{stats.cancelled}</div>
          <div className="label">Cancelled</div>
        </div>

        <div className="stat-card" style={{ cursor: 'pointer' }} onClick={() => navigate('/interviewer/evaluations')}>
          <div className="icon warning">
            <ClipboardList size={22} />
          </div>
          <div className="value">{stats.pending_evaluations}</div>
          <div className="label">Pending Eval</div>
        </div>
      </div>

      {/* Interview List */}
      <div className="card" style={{ marginTop: 24 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h3 style={{ fontSize: 18, fontWeight: 600, marginBottom: 2 }}>Scheduled Interviews</h3>
            <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>All confirmed bookings with your clients</p>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {(['all', 'upcoming', 'completed'] as FilterType[]).map((f) => (
              <button
                key={f}
                className={`tab${filter === f ? ' active' : ''}`}
                style={{ padding: '6px 16px', fontSize: 13 }}
                onClick={() => setFilter(f)}
              >
                {f === 'all' ? 'All' : f === 'upcoming' ? 'Upcoming' : 'Completed'}
              </button>
            ))}
          </div>
        </div>

        {filtered.length === 0 ? (
          <div className="empty-state" style={{ padding: '40px 20px' }}>
            <Calendar size={40} />
            <h3>No interviews found</h3>
            <p>Confirmed interviews will appear here once a client books a session.</p>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {filtered.map((item) => {
              const { date, time, isPast, isToday } = parseDate(item.scheduled_at)
              const displayName = item.client_name || item.client_email || 'Client'
              const statusLabel = isPast ? 'Completed' : isToday ? 'Today' : STATUS_LABEL[item.status] || 'Upcoming'
              const statusColor = isPast ? '#16a34a' : isToday ? '#d97706' : '#2563eb'

              return (
                <div
                  key={item.id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 16,
                    padding: '16px 0',
                    borderBottom: '1px solid var(--border)',
                  }}
                >
                  {/* Avatar */}
                  <div
                    className="avatar"
                    style={{
                      background: isToday ? 'var(--accent-gold)' : isPast ? '#9ca3af' : 'var(--primary-navy)',
                      flexShrink: 0,
                    }}
                  >
                    {displayName.charAt(0).toUpperCase()}
                  </div>

                  {/* Name + role */}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-dark)', marginBottom: 2 }}>
                      {displayName}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 6 }}>
                      <Clock size={12} />
                      {date}, {time} · {item.duration_minutes} min
                    </div>
                  </div>

                  {/* Status pill */}
                  <span style={{
                    fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20, whiteSpace: 'nowrap',
                    background: `${statusColor}18`, color: statusColor,
                  }}>
                    {statusLabel}
                  </span>

                  {/* Action buttons */}
                  <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                    {/* Prep button — always visible for any row with a client */}
                    {item.client_id && (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '7px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
                        onClick={() => navigate(`/interviewer/clients?prep=1&client=${item.client_id}`)}
                      >
                        <ClipboardList size={14} />
                        Prep
                      </button>
                    )}

                    {/* Start Interview (upcoming) / Evaluate (past) */}
                    {!isPast && item.zoom_join_url ? (
                      <button
                        className="btn btn-primary"
                        style={{ padding: '7px 16px', fontSize: 13, whiteSpace: 'nowrap' }}
                        onClick={() => navigate(`/interviewer/bookings/${item.id}`, {
                          state: {
                            client_name: item.client_name,
                            client_email: item.client_email,
                            zoom_url: item.zoom_join_url,
                            zoom_password: item.zoom_password,
                            scheduled_at: item.scheduled_at,
                            duration_minutes: item.duration_minutes,
                            status: item.status,
                          }
                        })}
                      >
                        <Video size={14} />
                        Start Interview
                      </button>
                    ) : isPast ? (
                      <button
                        className="btn btn-outline"
                        style={{ padding: '7px 14px', fontSize: 13, whiteSpace: 'nowrap' }}
                        onClick={() => openEvaluation(item)}
                      >
                        <ChevronRight size={14} />
                        Evaluate
                      </button>
                    ) : (
                      <button className="btn btn-primary" disabled style={{ padding: '7px 16px', fontSize: 13, opacity: 0.4, whiteSpace: 'nowrap' }}>
                        <Video size={14} />
                        Start Interview
                      </button>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
